import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import Reveal from "@/components/Reveal";
import Lightbox from "@/components/Lightbox";
import { useLang } from "@/i18n";
import { useContent } from "@/content/ContentContext";

const INITIAL = 18;

const GallerySection = () => {
  const { t } = useLang();
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const images = useContent().images.gallery.map((src) => ({
    src,
    alt: `${t.gallery.title} — U Magazin`,
  }));
  const visible = expanded ? images : images.slice(0, INITIAL);

  return (
    <section id="gallery" className="py-24 md:py-36" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <Reveal className="md:flex md:items-end md:justify-between gap-10">
          <div>
            <p className="eyebrow text-olive mb-7">
              {t.gallery.label}
            </p>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-2xl">
              {t.gallery.title}
            </h2>
          </div>
          <p className="hidden md:block text-[11px] uppercase tracking-[0.28em] text-anthracite/45 pb-3" data-testid="gallery-count">
            {visible.length} / {images.length}
          </p>
        </Reveal>

        <div className="mt-14 md:mt-20 columns-2 md:columns-3 gap-4 md:gap-6 [column-fill:balance]">
          {visible.map((img, i) => (
            <Reveal key={img.src} delay={0.04 * (i % 3)} className="mb-4 md:mb-6 break-inside-avoid">
              <button
                onClick={() => setSelected(i)}
                data-testid={`gallery-image-${i}`}
                className="frame block w-full group"
                aria-label={img.alt}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  data-edit-list="gallery"
                  data-edit-index={i}
                  className="w-full object-cover group-hover:brightness-105"
                />
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14 md:mt-16 flex justify-center">
          <button
            onClick={() => setExpanded((e) => !e)}
            data-testid="gallery-toggle"
            className="btn-outline"
          >
            {expanded ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {expanded ? t.gallery.showLess : `${t.gallery.showAll} (${images.length})`}
          </button>
        </Reveal>
      </div>

      <Lightbox
        images={visible}
        index={selected}
        onClose={() => setSelected(null)}
        onNavigate={setSelected}
      />
    </section>
  );
};

export default GallerySection;
